import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Button,
  CardFlags,
  Loading,
  NavBar,
  PaymentForm,
  UserInformationForm,
} from "components";
import usePayment from "hooks/usePayment";
import { useWhitelabel } from "hooks/useWhitelabel";
import { SdkPt } from "models";
import {
  EnumOriginPayment,
  EnumTypeDebit,
  MappedDebitsToGetInstallments,
} from "services/CreditCard";
import { useGetWhiteLabel } from "services/ParcelamosService";
import { Search } from "./Search";
import { EnumStages } from "./types";

import Order1 from "assets/images/icons/order-1.svg";
import Order2 from "assets/images/icons/order-2.svg";
import Order3 from "assets/images/icons/order-3.svg";
import PaymentCheckout from "components/PaymentForm/components/PaymentCheckout";
import PaymentSummary from "components/PaymentForm/components/ResumeSummary";
import { id_establishment_default } from "config/establishment";
import * as S from "./styles";
import { useIsMutating } from "react-query";

export const Billet = () => {
  const { id_usuario } = useParams();
  const { client } = useWhitelabel();
  const establishment_id = id_usuario ?? client?.id_usuario;
  const { isLoading } = useGetWhiteLabel(establishment_id as string);
  const [sdkPT, setSdkPT] = useState<SdkPt>({} as SdkPt);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [tokenRecaptcha, setTokenRecaptcha] = useState<string | null>(null);

  const { state } = useLocation();
  const activeMutatingInstallments = useIsMutating({ mutationKey: "get-installments" })

  const [stage, setStage] = useState<EnumStages>(EnumStages.SEARCH);
  const {
    paymentForm,
    dataPaymentBillet,
    setPaymentForm,
    getInstallments,
    clearPaymentBilletData,
  } = usePayment();

  const getIconStage = (stage: EnumStages) =>
    [Order1, Order2, Order3][stage - 1];

  const goBack = () => {
    setStage(stage - 1);
  };

  function handleChangeStage(nextStage: EnumStages) {
    if (nextStage === EnumStages.PAYMENT_FORM) {
      const debits: MappedDebitsToGetInstallments =
        dataPaymentBillet.billets.map((billet) => ({
          type: EnumTypeDebit.BILLET,
          id_bill: billet.id_bill,
        }));

      const request = {
        idEstablishment: client?.id_usuario || id_usuario,
        data: { origin: EnumOriginPayment.ONLINE, debits },
      };

      getInstallments(request, {
        onError(error: any) {
          toast.error(error?.response?.data?.message);
        },
      }).then(() => {
        setStage(nextStage);
      });
      return;
    }

    setStage(nextStage);
  }

  React.useEffect(() => {
    if (state) {
      handleChangeStage(state.stage);
    }
  }, [state]);

  React.useEffect(() => {
    return clearPaymentBilletData;
  }, []);

  return (
    <Loading isLoading={isLoading} size="large" backgroundNone>
      <NavBar />
      <S.Wrapper>
        <S.Button>
          {![EnumStages.SEARCH, EnumStages.ORDER_DETAILS, EnumStages.PAYMENT_METHODS].includes(stage) &&
            <Button
              title={`Voltar para etapa`}
              type_button="outlined"
              icon_right={getIconStage(stage - 1)}
              onClick={goBack}
              disabled={buttonDisabled}
            />
          }
        </S.Button>
      </S.Wrapper>

      <S.WrapperStages>
        <Search
          setStage={handleChangeStage}
          enable={stage === EnumStages.SEARCH}
          hasBilletActions={client?.action_button_billet}
          idEstablishment={
            id_usuario ?? client?.id_usuario ?? id_establishment_default
          }
        />
        <UserInformationForm
          enable={stage === EnumStages.CLIENT_IDENTIFICATION}
          setStage={handleChangeStage}
          stage={stage}
        />
        <PaymentForm
          setStage={handleChangeStage}
          paymentForm={paymentForm}
          setPaymentForm={setPaymentForm}
          enable={stage === EnumStages.PAYMENT_FORM}
          setSdkPT={setSdkPT}
        />
        {stage === EnumStages.SUMMARY && (
          <PaymentSummary
            route="boleto"
            stage={stage}
            onBack={goBack}
            isLoadingInstallments={!!activeMutatingInstallments}
            nextStage={() => handleChangeStage(EnumStages.PAYMENT_METHODS)}
            setTokenRecaptcha={setTokenRecaptcha}
            tokenRecaptcha={tokenRecaptcha}
          />
        )}
        {stage === EnumStages.PAYMENT_METHODS && (
          <PaymentCheckout
            stage={stage}
            route="boleto"
            tokenRecaptcha={tokenRecaptcha}
            setTokenRecaptcha={setTokenRecaptcha}
          />
        )}

        {stage !== EnumStages.ORDER_DETAILS ? <CardFlags /> : null}
      </S.WrapperStages>
    </Loading>
  );
};
